import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import Logo from '../Logo/Logo'
import supabase from '../../utils/supabase'

const ResetPassword: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const redirectTimeoutRef = useRef<number | null>(null)

  // Check if current language is RTL
  const isRTL = i18n.language === 'he' || i18n.language === 'ar'

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'))
      setLoading(false)
      return
    }

    if (password.length < 6) {
      setError(t('auth.passwordTooShort'))
      setLoading(false)
      return
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      })

      if (error) {
        setError(error.message)
      } else {
        setMessage(t('auth.passwordUpdatedSuccessfully'))
        // Clear the form
        setPassword('')
        setConfirmPassword('')

        // Remove recovery hash/tokens from the URL.
        window.history.replaceState(null, '', window.location.pathname + window.location.search)

        // Obtain a fresh backend JWT using the current (post-update) Supabase session,
        // then navigate directly to the dashboard without forcing a sign-out + re-login.
        // The previous forced signout+re-login was the root cause of the bug: handleSignIn
        // would call supabase.auth.signOut() when the backend JWT call failed mid-flight,
        // kicking the user out of the app even after a successful Supabase authentication.
        redirectTimeoutRef.current = window.setTimeout(async () => {
          try {
            const { data: { session: currentSession } } = await supabase.auth.getSession()
            if (currentSession?.access_token) {
              const w: any = window as any
              const baseUrl = w._BACKEND_BASE_URL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'
              const res = await fetch(`${baseUrl}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: currentSession.access_token }),
                credentials: 'include',
              })
              if (res.ok) {
                const data = await res.json()
                if (data.access_token) {
                  localStorage.setItem('backend_access_token', data.access_token)
                  window.dispatchEvent(new Event('storage'))
                  window.dispatchEvent(new CustomEvent('backend-token-updated', {
                    detail: { role: data.user?.role, center_id: data.user?.center_id }
                  }))
                }
              } else {
                console.warn('[ResetPassword] Backend JWT refresh returned', res.status)
              }
            }
          } catch (backendErr) {
            console.warn('[ResetPassword] Backend JWT refresh failed:', backendErr)
          }
          navigate('/', { replace: true })
        }, 2000)
      }
    } catch (err) {
      setError(t('auth.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current !== null) {
        window.clearTimeout(redirectTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900 font-inter" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-md w-full space-y-8 p-8">
        {/* Logo and Title */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <Logo size="md" />
          </div>
          <div className="mb-4">
            <h3 className="text-xl font-semibold text-neutral-800 dark:text-neutral-200 mb-1">
              {t('auth.resetPassword')}
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {t('auth.resetPasswordSubtitle')}
            </p>
            {user && (
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
                {t('auth.loggedInAs')}: {user.email}
              </p>
            )}
          </div>
        </div>

        {/* Error and Success Messages */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {message && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <p className="text-sm text-green-600 dark:text-green-400 mb-2">{message}</p>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto mb-2"></div>
            <p className="text-xs text-green-500 dark:text-green-400 text-center">
              {t('auth.redirectingToDashboard')}
            </p>
          </div>
        )}

        {/* Password Reset Form */}
        <div className="mt-8">
          <form onSubmit={handlePasswordReset} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                {t('auth.newPassword')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.newPasswordPlaceholder')}
                  required
                  dir="ltr"
                  className="w-full px-3 py-2 pr-11 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 cursor-pointer"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <Eye className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                {t('auth.confirmNewPassword')}
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('auth.confirmNewPasswordPlaceholder')}
                  required
                  dir="ltr"
                  className="w-full px-3 py-2 pr-11 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 cursor-pointer"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <Eye className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
            >
              {loading ? t('auth.updatingPassword') : t('auth.updatePassword')}
            </button>
          </form>

          {/* Password Requirements */}
          <div className="mt-6 p-4 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
            <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              {t('auth.passwordRequirements')}
            </h4>
            <ul className="text-xs text-neutral-600 dark:text-neutral-400 space-y-1">
              <li>• {t('auth.passwordMinLength')}</li>
              <li>• {t('auth.passwordShouldMatch')}</li>
              <li>• {t('auth.passwordRecommendation')}</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-neutral-500 dark:text-neutral-400">
          <p>{t('auth.footer')}</p>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword
